import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { FaPlus, FaSearch, FaEye, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { getEngineers, deleteEngineers, toggleEngineerStatus } from '../../../../redux/Slices/EngineersSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };
  if (Array.isArray(payload?.data?.data)) return { items: payload.data.data, pagination: payload.data };
  if (Array.isArray(payload?.data)) return { items: payload.data, pagination: payload.pagination || payload.meta || null };
  if (Array.isArray(payload?.items)) return { items: payload.items, pagination: payload.pagination || payload.meta || null };
  if (Array.isArray(payload)) return { items: payload, pagination: null };
  return { items: [], pagination: payload.pagination || payload.meta || null };
};

const Engineers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [engineers, setEngineers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [engineerToDelete, setEngineerToDelete] = useState(null);
  const isRTL = i18n.language === 'ar';

  const paginationInfo = useMemo(() => {
    if (!pagination) return { current_page: currentPage, last_page: 1 };
    return {
      current_page: pagination.current_page || pagination.page || currentPage,
      last_page: pagination.last_page || pagination.total_pages || 1,
    };
  }, [pagination, currentPage]);

  const fetchEngineers = useCallback(
    async (page = currentPage, search = searchTerm, status = statusFilter) => {
      try {
        setIsLoading(true);
        const payload = await dispatch(
          getEngineers({
            page,
            per_page: 10,
            search: search || undefined,
            is_active: status !== '' ? status : undefined,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setEngineers(items);
        setPagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('engineers.messages.fetchError'), { rtl: isRTL });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, currentPage, searchTerm, statusFilter]
  );

  useEffect(() => {
    fetchEngineers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTitle('sidenav.engineers');
  }, [setTitle]);

  useEffect(() => {
    fetchEngineers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    fetchEngineers(1, value, statusFilter);
  };

  const handleDelete = async () => {
    if (!engineerToDelete) return;
    try {
      await dispatch(deleteEngineers(engineerToDelete.id)).unwrap();
      toast.success(t('engineers.messages.deleteSuccess'), { rtl: isRTL });
      setShowDeleteModal(false);
      setEngineerToDelete(null);
      fetchEngineers(currentPage);
    } catch (error) {
      toast.error(error || t('engineers.messages.deleteError'), { rtl: isRTL });
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await dispatch(toggleEngineerStatus(id)).unwrap();
      toast.success(t('engineers.messages.toggleSuccess'), { rtl: isRTL });
      fetchEngineers(currentPage);
    } catch (error) {
      toast.error(error || t('engineers.messages.toggleError'), { rtl: isRTL });
    }
  };

  return (
    <div style={{ padding: 30, backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
          <FaSearch style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 16, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('engineers.searchPlaceholder')}
            style={{
              width: '100%',
              padding: isRTL ? '12px 42px 12px 16px' : '12px 16px 12px 42px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: 8,
              color: 'white',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            minWidth: 140,
            padding: '12px',
            backgroundColor: '#1a1f2e',
            border: '1px solid #333',
            borderRadius: 8,
            color: 'white',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
          }}
        >
          <option value="">{t('engineers.allStatuses')}</option>
          <option value="1">{t('engineers.active')}</option>
          <option value="0">{t('engineers.inactive')}</option>
        </select>
        <button
          onClick={() => navigate('/engineers/add')}
          style={{
            backgroundColor: '#AC2000',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <FaPlus /> {t('engineers.addNew')}
        </button>
      </div>

      <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                {[
                  { text: t('engineers.tableHeaders.number'), align: 'center' },
                  { text: t('engineers.tableHeaders.name'), align: isRTL ? 'right' : 'left' },
                  { text: t('engineers.tableHeaders.email'), align: isRTL ? 'right' : 'left' },
                  { text: t('engineers.tableHeaders.phone'), align: isRTL ? 'right' : 'left' },
                  { text: t('engineers.tableHeaders.status'), align: 'center' },
                  { text: t('engineers.tableHeaders.actions'), align: 'center' }
                ].map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: 14,
                      textAlign: header.align,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: 14,
                      borderBottom: '1px solid #333',
                    }}
                  >
                    {header.text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#ccc' }}>
                    {t('engineers.loading')}
                  </td>
                </tr>
              ) : engineers.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#ccc' }}>
                    {t('engineers.noData')}
                  </td>
                </tr>
              ) : (
                engineers.map((engineer, index) => (
                  <tr key={engineer.id || index} style={{ borderBottom: '1px solid #333' }}>
                    <td style={cellStyle}>{(paginationInfo.current_page - 1) * 10 + (index + 1)}</td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL 
                        ? (engineer.name_ar || engineer.name_en || '—')
                        : (engineer.name_en || engineer.name_ar || '—')}
                    </td>
                    <td style={{ ...cellStyle, direction: 'ltr', textAlign: 'left' }}>{engineer.email || '—'}</td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{engineer.phone || '—'}</td>
                    <td style={cellStyle}>
                      <span
                        style={{
                          padding: '4px 12px',
                          borderRadius: 20,
                          backgroundColor: engineer.is_active ? 'rgba(39,174,96,0.15)' : 'rgba(192,57,43,0.2)',
                          color: engineer.is_active ? '#2ecc71' : '#e74c3c',
                          fontWeight: 'bold',
                          fontSize: 12,
                        }}
                      >
                        {engineer.is_active ? t('engineers.active') : t('engineers.inactive')}
                      </span>
                    </td>
                    <td style={{ ...cellStyle, minWidth: 200 }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'nowrap' }}>
                        <ActionButton title={t('engineers.actions.view')} onClick={() => navigate(`/engineers/view/${engineer.id}`)}>
                          <FaEye />
                        </ActionButton>
                        <ActionButton title={t('engineers.actions.edit')} onClick={() => navigate(`/engineers/edit/${engineer.id}`)}>
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t('engineers.actions.delete')}
                          bg="#c0392b"
                          onClick={() => {
                            setEngineerToDelete(engineer);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                        <ActionButton
                          title={t('engineers.actions.toggleStatus')}
                          bg="#34495e"
                          onClick={() => handleToggleStatus(engineer.id)}
                        >
                          {engineer.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16, borderTop: '1px solid #333' }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={paginationInfo.current_page === 1}
            style={paginationButton(paginationInfo.current_page === 1)}
          >
            {t('engineers.previous')}
          </button>
          <span style={{ padding: 8 }}>
            {paginationInfo.current_page} / {paginationInfo.last_page}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => (prev >= paginationInfo.last_page ? paginationInfo.last_page : prev + 1))
            }
            disabled={paginationInfo.current_page >= paginationInfo.last_page}
            style={paginationButton(paginationInfo.current_page >= paginationInfo.last_page)}
          >
            {t('engineers.next')}
          </button>
        </div>
      </div>
      {showDeleteModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalCardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t('engineers.deleteModal.title')}</h3>
            <p style={{ marginBottom: 24 }}>
              {t('engineers.deleteModal.message')}{' '}
              <strong>{isRTL 
                ? (engineerToDelete?.name_ar || engineerToDelete?.name_en)
                : (engineerToDelete?.name_en || engineerToDelete?.name_ar)}</strong>؟
            </p>
            <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start', gap: 12, flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setShowDeleteModal(false);
                  setEngineerToDelete(null);
                }}
              >
                {t('engineers.deleteModal.cancel')}
              </button>
              <button style={modalPrimaryButtonStyle} onClick={handleDelete}>
                {t('engineers.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cellStyle = {
  padding: 14,
  textAlign: 'center',
  color: 'white',
  fontSize: 14,
};

const paginationButton = (disabled) => ({
  padding: '8px 16px',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const ActionButton = ({ children, onClick, title, bg = '#2f3644' }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: 6,
      padding: '8px 10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 38,
    }}
  >
    {children}
  </button>
);

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  width: '100%',
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

const modalSecondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

export default Engineers;

